import axios, { AxiosError } from 'axios';
import { throttle } from 'lodash';
import qs from 'qs';
import LoadingService from '@/dossier/services/LoadingService';
import ConfigService from '@/shared/services/ConfigService';
import i18n from '../i18n';
import router from '../router';
import { goto } from '../router/navigation';
import AuthService from '../services/AuthService';
import { AuthenticationPending } from '../services/AzureAuthService';
import Modals from '../services/Modals';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipLoader?: boolean;
  }
}

const refreshAzureTokenThrottled = throttle(() => AuthService.loginWithAzure(), 60000, { leading: true, trailing: false });

export default function setupAxios(): void {
  axios.defaults.baseURL = ConfigService.get('API_BASE_URL');
  axios.defaults.paramsSerializer = params => {
    return qs.stringify(params, { arrayFormat: 'repeat' });
  };
  setupInterceptors();
}

function setupInterceptors(): void {
  axios.interceptors.request.use(async (config) => {
    if (!config.skipLoader) LoadingService.start();
    return config;
  });

  axios.interceptors.response.use(async (response) => {
    if (!response.config.skipLoader) await LoadingService.stop();
    return response;
  }, handleError);
}

async function handleError(error: any): Promise<any> {
  await LoadingService.stop();

  if (isAxiosError(error) && error.response) {
    const status = Number(error.response.status);
    const data = error.response.data as any;
    const errors = Array.isArray(data.errors) ? data.errors : [];

    if (status === 401) {
      if (errors.includes('ReauthenticationNeeded')) {
        return handleAzureTokenExpired(error);
      } else {
        AuthService.logout();
        await goto({ name: 'login' }, router);
      }
    } else if (status === 403 && error.request.url === '/api/mobopro/v1/dmsrepairorders') {
      Modals.error(i18n.global.t('general.dmsImportNotAvailable'));
    } else if (error.config?.url === '/api/mobopro/v1/search') {
      throw error;
    } else {
      // @ts-ignore
      const message = errors.join('\n') || i18n.global.t('general.error');
      Modals.error(message);
    }
  }

  throw error;
}

async function handleAzureTokenExpired(error: AxiosError): Promise<any> {
  try {
    await refreshAzureTokenThrottled();
    return axios.request(error.config!);
  } catch (nextError) {
    if (nextError instanceof AuthenticationPending) {
      // this is normal - Azure redirect initiated
    } else {
      throw nextError;
    }
  }
}

function isAxiosError(error: any): error is AxiosError {
  return error?.isAxiosError;
}